// Copyright 2025 Sorama B.V.

import { autoinject, bindable, computedFrom } from "aurelia-framework";
import { FileDownloader } from "libraries/FileDownloader/FileDownloader";
import { CamIV64DeviceType } from "services/Hardware/CamHTTP/CamIV64Helpers";

const featuresList = [
    "Sound localisation",
    "Frequency selection (up to 60kHz)",
    "Frequency selection (up to 28kHz)",
    "Internal storage",
    "Recording time",
    "Data transferring",
    "Device & licenses updates",
    "Wi-Fi",
    "Video recording",
    "Image capture",
    "Severity index",
    "Pin max/min dB",
    "High sample rate (up to 120kHz)",
    "Adjustable blob size",
    "Color palette",
    "Inspection preview",
    "Photo notes",
    "Reports",
    "Zoom",
    "Sound marker",
    "Leak rate",
    "Leak calculation",
    "Leak rate conversion",
    "Leak inspection - gas type set up",
    "PRPD plot",
    "Estimated PD type",
    "Basic mechanical inspection",
    "Multi-leak inspection",
    "Multi-leak cost calculation",
    "Mechanical inspection upgrade",
    "Anomaly detection",
    "Distance",
    "Basic SLM",
    "Advanced SLM",
    "Spectrogram",
    "NTi integration",
    "Reverberation Time",
    "Speech Level Reduction",
    "Sound Reduction Index",
    "Leak scan",
    "Leak size indication",
] as const;
export type FeatureName = (typeof featuresList)[number];
export type ShopifyVariables = {
    productId: string;
    variantId: {
        annually: {
            iV64s?: string;
            iV64Ex?: string;
            iV64?: string;
        };
        lifetime: {
            iV64s?: string;
            iV64Ex?: string;
            iV64?: string;
        };
    };
};

@autoinject
export class PackagesComparisonTable {
    @bindable
    public deviceType: CamIV64DeviceType;
    @bindable
    public isLifetime: boolean;

    private gearIcon = "/resources/images/icons/gears.svg";
    private pipeIcon = "/resources/images/icons/pipe.svg";
    private trophyIcon = "/resources/images/icons/trophy.svg";
    private boltIcon = "/resources/images/icons/bolt.svg";
    @computedFrom("deviceType")
    public get features(): FeatureName[] {
        const featuresMap = this.deviceType === "iV64" ? this.iV64PackagesFeatures : this.packagesFeatures;
        const allPackageFeatures = new Set(Object.values(featuresMap).flat());
        return [...featuresList].filter((f) => allPackageFeatures.has(f));
    }

    @computedFrom("deviceType")
    public get isIV64(): boolean {
        return this.deviceType === "iV64";
    }

    private readonly iV64PackageValueMap: Record<string, Partial<Record<FeatureName, string>>> = {
        Core: {
            "Frequency selection (up to 28kHz)": "Narrow",
            "Internal storage": "8 GB",
            "Recording time": "up to 10s",
            "Data transferring": "USB & Sorama Portal",
            "Device & licenses updates": "on device & Sorama Portal",
        },
        Advanced: {
            "Frequency selection (up to 28kHz)": "Extended",
            "Internal storage": "500 GB",
            "Recording time": "Unlimited",
            "Data transferring": "USB & Sorama Portal",
            "Device & licenses updates": "on device & Sorama Portal",
        },
        Interior: {
            "Frequency selection (up to 28kHz)": "Extended",
            "Internal storage": "500 GB",
            "Recording time": "Unlimited",
            "Data transferring": "USB & Sorama Portal",
            "Device & licenses updates": "on device & Sorama Portal",
        },
        Exterior: {
            "Frequency selection (up to 28kHz)": "Extended",
            "Internal storage": "500 GB",
            "Recording time": "Unlimited",
            "Data transferring": "USB & Sorama Portal",
            "Device & licenses updates": "on device & Sorama Portal",
        },
        "Ultimate Suite": {
            "Frequency selection (up to 28kHz)": "Extended",
            "Internal storage": "500 GB",
            "Recording time": "Unlimited",
            "Data transferring": "USB & Sorama Portal",
            "Device & licenses updates": "on device & Sorama Portal",
        },
    };

    private readonly packageValueMap: Record<string, Partial<Record<FeatureName, string>>> = {
        Core: {
            "Frequency selection (up to 60kHz)": "Narrow",
            "Internal storage": "8 GB",
            "Recording time": "up to 10s",
            "Data transferring": "USB",
            "Device & licenses updates": "on device",
        },
        Advanced: {
            "Frequency selection (up to 60kHz)": "Extended",
            "Internal storage": "500 GB",
            "Recording time": "Unlimited",
            "Data transferring": "USB, Wi-Fi & Sorama Portal",
            "Device & licenses updates": "on device & Sorama Portal",
        },
        "Leak Inspection": {
            "Frequency selection (up to 60kHz)": "Extended",
            "Internal storage": "500 GB",
            "Recording time": "Unlimited",
            "Data transferring": "USB, Wi-Fi & Sorama Portal",
            "Device & licenses updates": "on device & Sorama Portal",
        },
        "Partial discharge inspection": {
            "Frequency selection (up to 60kHz)": "Extended",
            "Internal storage": "500 GB",
            "Recording time": "Unlimited",
            "Data transferring": "USB, Wi-Fi & Sorama Portal",
            "Device & licenses updates": "on device & Sorama Portal",
        },
        "Mechanical Inspection": {
            "Frequency selection (up to 60kHz)": "Extended",
            "Internal storage": "500 GB",
            "Recording time": "Unlimited",
            "Data transferring": "USB, Wi-Fi & Sorama Portal",
            "Device & licenses updates": "on device & Sorama Portal",
        },
        Ultimate: {
            "Frequency selection (up to 60kHz)": "Extended",
            "Internal storage": "500 GB",
            "Recording time": "Unlimited",
            "Data transferring": "USB, Wi-Fi & Sorama Portal",
            "Device & licenses updates": "on device & Sorama Portal",
        },
    };
    private readonly shopifyPackages: Record<string, ShopifyVariables> = {
        Advanced: {
            productId: "10287917105485",
            variantId: {
                annually: {
                    iV64s: "52469427568973",
                    iV64Ex: "52469475443021",
                    iV64: "53863730676045",
                },
                lifetime: {
                    iV64s: "52469427601741",
                    iV64Ex: "52469475475789",
                    iV64: "53863730708813",
                },
            },
        },
        "Leak Inspection": {
            productId: "10287944565069",
            variantId: {
                annually: {
                    iV64s: "52469476196685",
                    iV64Ex: "52469476262221",
                },
                lifetime: {
                    iV64s: "52469476229453",
                    iV64Ex: "52469476294989",
                },
            },
        },
        "Partial discharge inspection": {
            productId: "10287945384269",
            variantId: {
                annually: {
                    iV64s: "52469477409101",
                    iV64Ex: "52469477474637",
                },
                lifetime: {
                    iV64s: "52469477441869",
                    iV64Ex: "52469477507405",
                },
            },
        },
        "Mechanical Inspection": {
            productId: "10287946137933",
            variantId: {
                annually: {
                    iV64s: "52469478687053",
                    iV64Ex: "52469478752589",
                },
                lifetime: {
                    iV64s: "52469478719821",
                    iV64Ex: "52469478785357",
                },
            },
        },
        Ultimate: {
            productId: "10287949349197",
            variantId: {
                annually: {
                    iV64s: "52469482455373",
                    iV64Ex: "52469482520909",
                },
                lifetime: {
                    iV64s: "52469482488141",
                    iV64Ex: "52469482553677",
                },
            },
        },
    };

    private readonly iV64ShopifyPackages: Record<string, ShopifyVariables> = {
        Advanced: {
            productId: "10287917105485",
            variantId: {
                annually: { iV64: "53863730676045" },
                lifetime: { iV64: "53863730708813" },
            },
        },
        Interior: {
            productId: "",
            variantId: { annually: {}, lifetime: {} },
        },
        Exterior: {
            productId: "",
            variantId: { annually: {}, lifetime: {} },
        },
        "Ultimate Suite": {
            productId: "",
            variantId: { annually: {}, lifetime: {} },
        },
    };

    private readonly iV64PackagesFeatures: Record<string, FeatureName[]> = {
        Core: [
            "Sound localisation",
            "Frequency selection (up to 28kHz)",
            "Internal storage",
            "Recording time",
            "Data transferring",
            "Device & licenses updates",
            "Wi-Fi",
            "Image capture",
            "Video recording",
            "Distance",
        ],
        Advanced: [
            "Sound localisation",
            "Frequency selection (up to 28kHz)",
            "Internal storage",
            "Recording time",
            "Data transferring",
            "Device & licenses updates",
            "Wi-Fi",
            "Image capture",
            "Video recording",
            "Distance",
            "Adjustable blob size",
            "Color palette",
            "Photo notes",
            "Pin max/min dB",
            "Zoom",
            "Sound marker",
            "Basic SLM",
            "Spectrogram",
        ],
        Interior: [
            "Sound localisation",
            "Frequency selection (up to 28kHz)",
            "Internal storage",
            "Recording time",
            "Data transferring",
            "Device & licenses updates",
            "Wi-Fi",
            "Image capture",
            "Video recording",
            "Distance",
            "Adjustable blob size",
            "Color palette",
            "Photo notes",
            "Reports",
            "Pin max/min dB",
            "Zoom",
            "Sound marker",
            "Basic SLM",
            "Spectrogram",
            "Advanced SLM",
            "NTi integration",
            "Reverberation Time",
            "Speech Level Reduction",
            "Sound Reduction Index",
        ],
        Exterior: [
            "Sound localisation",
            "Frequency selection (up to 28kHz)",
            "Internal storage",
            "Recording time",
            "Data transferring",
            "Device & licenses updates",
            "Wi-Fi",
            "Image capture",
            "Video recording",
            "Distance",
            "Adjustable blob size",
            "Color palette",
            "Photo notes",
            "Reports",
            "Pin max/min dB",
            "Zoom",
            "Sound marker",
            "Basic SLM",
            "Spectrogram",
            "Advanced SLM",
            "Leak scan",
            "Leak size indication",
        ],
        "Ultimate Suite": [
            "Sound localisation",
            "Frequency selection (up to 28kHz)",
            "Internal storage",
            "Recording time",
            "Data transferring",
            "Device & licenses updates",
            "Wi-Fi",
            "Image capture",
            "Video recording",
            "Distance",
            "Adjustable blob size",
            "Color palette",
            "Photo notes",
            "Reports",
            "Pin max/min dB",
            "Zoom",
            "Sound marker",
            "Basic SLM",
            "Spectrogram",
            "Advanced SLM",
            "NTi integration",
            "Reverberation Time",
            "Speech Level Reduction",
            "Sound Reduction Index",
            "Leak scan",
            "Leak size indication",
        ],
    };

    private readonly packagesFeatures: Record<string, FeatureName[]> = {
        Core: [
            "Sound localisation",
            "Frequency selection (up to 60kHz)",
            "Internal storage",
            "Recording time",
            "Data transferring",
            "Device & licenses updates",
            "Wi-Fi",
            "Video recording",
            "Image capture",
            "Severity index",
        ],
        Advanced: [
            "Sound localisation",
            "Frequency selection (up to 60kHz)",
            "Internal storage",
            "Recording time",
            "Data transferring",
            "Device & licenses updates",
            "Wi-Fi",
            "Video recording",
            "Image capture",
            "Severity index",
            "Pin max/min dB",
            "High sample rate (up to 120kHz)",
            "Adjustable blob size",
            "Color palette",
            "Inspection preview",
            "Photo notes",
            "Reports",
            "Zoom",
            "Sound marker",
            "Leak rate",
            "Leak calculation",
            "Leak inspection - gas type set up",
            "PRPD plot",
            "Estimated PD type",
            "Basic mechanical inspection",
        ],
        "Leak Inspection": [
            "Sound localisation",
            "Frequency selection (up to 60kHz)",
            "Internal storage",
            "Recording time",
            "Data transferring",
            "Device & licenses updates",
            "Wi-Fi",
            "Video recording",
            "Image capture",
            "Severity index",
            "Pin max/min dB",
            "High sample rate (up to 120kHz)",
            "Adjustable blob size",
            "Color palette",
            "Inspection preview",
            "Photo notes",
            "Reports",
            "Zoom",
            "Sound marker",
            "Leak rate",
            "Leak calculation",
            "Leak rate conversion",
            "Leak inspection - gas type set up",
            "PRPD plot",
            "Estimated PD type",
            "Basic mechanical inspection",
            "Multi-leak inspection",
            "Multi-leak cost calculation",
        ],
        "Partial discharge inspection": [
            "Sound localisation",
            "Frequency selection (up to 60kHz)",
            "Internal storage",
            "Recording time",
            "Data transferring",
            "Device & licenses updates",
            "Wi-Fi",
            "Video recording",
            "Image capture",
            "Severity index",
            "Pin max/min dB",
            "High sample rate (up to 120kHz)",
            "Adjustable blob size",
            "Color palette",
            "Inspection preview",
            "Photo notes",
            "Reports",
            "Zoom",
            "Sound marker",
            "Leak rate",
            "Leak calculation",
            "Leak inspection - gas type set up",
            "PRPD plot",
            "Estimated PD type",
            "Basic mechanical inspection",
        ],
        "Mechanical Inspection": [
            "Sound localisation",
            "Frequency selection (up to 60kHz)",
            "Internal storage",
            "Recording time",
            "Data transferring",
            "Device & licenses updates",
            "Wi-Fi",
            "Video recording",
            "Image capture",
            "Severity index",
            "Pin max/min dB",
            "High sample rate (up to 120kHz)",
            "Adjustable blob size",
            "Color palette",
            "Inspection preview",
            "Photo notes",
            "Reports",
            "Zoom",
            "Sound marker",
            "Leak rate",
            "Leak calculation",
            "Leak inspection - gas type set up",
            "PRPD plot",
            "Estimated PD type",
            "Basic mechanical inspection",
            "Mechanical inspection upgrade",
            "Anomaly detection",
        ],
        Ultimate: [
            "Sound localisation",
            "Frequency selection (up to 60kHz)",
            "Internal storage",
            "Recording time",
            "Data transferring",
            "Device & licenses updates",
            "Wi-Fi",
            "Video recording",
            "Image capture",
            "Severity index",
            "Pin max/min dB",
            "High sample rate (up to 120kHz)",
            "Adjustable blob size",
            "Color palette",
            "Inspection preview",
            "Photo notes",
            "Reports",
            "Zoom",
            "Sound marker",
            "Leak rate",
            "Leak calculation",
            "Leak rate conversion",
            "Leak inspection - gas type set up",
            "PRPD plot",
            "Estimated PD type",
            "Basic mechanical inspection",
            "Multi-leak inspection",
            "Multi-leak cost calculation",
            "Mechanical inspection upgrade",
            "Anomaly detection",
        ],
        Expert: [],
    };
    private shopifyStyle = {
        button: {
            "font-size": "12px",
            padding: "0.5rem 1rem",
            "border-radius": "3px",
            transition: "all 0.2s ease",
            "background-color": "#a1b2c4",
            color: "#2d3e50",
        },
        buttonWrapper: {
            "margin-top": "0px",
            "margin-bottom": "1rem",
            "text-align": "center",
        },
    };

    private getFeatureDisplay(packageName: string, feature: FeatureName): string {
        const featuresMap = this.deviceType === "iV64" ? this.iV64PackagesFeatures : this.packagesFeatures;
        const valueMap = this.deviceType === "iV64" ? this.iV64PackageValueMap : this.packageValueMap;
        const featureList: FeatureName[] = featuresMap[packageName] ?? [];
        if (!featureList.includes(feature)) return "";
        const value = valueMap[packageName]?.[feature];
        if (value) return value;
        return '<i class="fa fa-check" style="color: #A1B2C4"></i>';
    }

    protected downloadPackageComparison(): void {
        if (!this.deviceType) return;

        const fileName = `Package comparison - Sorama CAM ${this.deviceType}.pdf`;
        FileDownloader.downloadAsFileFromURL(`resources/documents/webshop/${fileName}`, fileName);
    }

    private getShopifyPackageVariantId(packageName: string, isLifetime: boolean): string {
        const shopifyMap = this.deviceType === "iV64" ? this.iV64ShopifyPackages : this.shopifyPackages;
        const shopifyPackageInfo = shopifyMap[packageName];
        if (!shopifyPackageInfo) return "";
        if (this.deviceType === "iV64") {
            return isLifetime ? shopifyPackageInfo.variantId.lifetime.iV64 : shopifyPackageInfo.variantId.annually.iV64;
        } else if (this.deviceType === "iV64Ex") {
            return isLifetime
                ? shopifyPackageInfo.variantId.lifetime.iV64Ex
                : shopifyPackageInfo.variantId.annually.iV64Ex;
        } else {
            return isLifetime
                ? shopifyPackageInfo.variantId.lifetime.iV64s
                : shopifyPackageInfo.variantId.annually.iV64s;
        }
    }
    private getShopifyPackageProductId(packageName: string): string {
        const shopifyMap = this.deviceType === "iV64" ? this.iV64ShopifyPackages : this.shopifyPackages;
        return shopifyMap[packageName]?.productId ?? "";
    }
    private isPackageDisabled(packageName: string): boolean {
        return !this.getShopifyPackageProductId(packageName);
    }
}
